import React, { useState, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';

const RolesPermissions = () => {
  const { showSuccess, showError } = useApp();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // Permisos del sistema (estáticos)
  const systemPermissions = [
    { id: 'all', name: 'Acceso Total', description: 'Acceso completo a todas las funcionalidades' },
    { id: 'dashboard', name: 'Dashboard', description: 'Ver panel principal' },
    { id: 'projects', name: 'Proyectos ABP', description: 'Gestionar proyectos de aprendizaje basado en proyectos' },
    { id: 'courses', name: 'Cursos y Materias', description: 'Administrar cursos y materias' },
    { id: 'evaluations', name: 'Evaluaciones', description: 'Crear y gestionar evaluaciones' },
    { id: 'grades', name: 'Notas', description: 'Ver y gestionar calificaciones' },
    { id: 'users', name: 'Usuarios', description: 'Administrar usuarios del sistema' },
    { id: 'calendar', name: 'Calendario', description: 'Acceder al calendario de actividades' },
    { id: 'messages', name: 'Mensajería', description: 'Enviar y recibir mensajes' },
    { id: 'reports', name: 'Reportes', description: 'Generar y visualizar reportes' },
    { id: 'payments', name: 'Pagos y Finanzas', description: 'Gestionar pagos y finanzas' }
  ];

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      // Mock data - aquí se haría la llamada real a la API
      const mockRoles = [
        {
          id: 1,
          name: 'Administrador',
          description: 'Control total del sistema',
          permissions: ['all'],
          isSystem: true,
          userCount: 3
        },
        {
          id: 2,
          name: 'Profesor',
          description: 'Gestión de proyectos y evaluaciones',
          permissions: ['dashboard', 'projects', 'evaluations', 'courses', 'calendar', 'messages'],
          isSystem: true,
          userCount: 25
        },
        {
          id: 3,
          name: 'Alumno',
          description: 'Acceso a proyectos y calificaciones',
          permissions: ['dashboard', 'projects', 'grades', 'messages'],
          isSystem: true,
          userCount: 350
        },
        {
          id: 4,
          name: 'Apoderado',
          description: 'Ver información de estudiantes',
          permissions: ['dashboard', 'grades', 'messages', 'reports', 'payments'],
          isSystem: true,
          userCount: 280
        }
      ];
      setRoles(mockRoles);
    } catch (error) {
      showError('Error al cargar los roles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleName(role.name);
      setRoleDescription(role.description);
      setSelectedPermissions(role.permissions);
    } else {
      setEditingRole(null);
      setRoleName('');
      setRoleDescription('');
      setSelectedPermissions([]);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions([]);
  };

  const handleTogglePermission = (permissionId) => {
    if (permissionId === 'all') {
      // Si selecciona "all", limpiar todos los demás
      if (selectedPermissions.includes('all')) {
        setSelectedPermissions([]);
      } else {
        setSelectedPermissions(['all']);
      }
    } else {
      // Si selecciona otro permiso, quitar "all"
      let newPermissions = selectedPermissions.filter(p => p !== 'all');

      if (newPermissions.includes(permissionId)) {
        newPermissions = newPermissions.filter(p => p !== permissionId);
      } else {
        newPermissions.push(permissionId);
      }

      setSelectedPermissions(newPermissions);
    }
  };

  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      showError('El nombre del rol es requerido');
      return;
    }

    if (selectedPermissions.length === 0) {
      showError('Debes seleccionar al menos un permiso');
      return;
    }

    try {
      // Aquí se enviaría a la API
      const roleData = {
        name: roleName,
        description: roleDescription,
        permissions: selectedPermissions
      };

      if (editingRole) {
        // Actualizar rol existente
        showSuccess('Rol actualizado correctamente');
      } else {
        // Crear nuevo rol
        showSuccess('Rol creado correctamente');
      }

      handleCloseModal();
      loadRoles();
    } catch (error) {
      showError('Error al guardar el rol');
    }
  };

  const handleDeleteRole = async (roleId) => {
    const role = roles.find(r => r.id === roleId);

    if (role.isSystem) {
      showError('No se pueden eliminar roles del sistema');
      return;
    }

    if (role.userCount > 0) {
      showError('No se puede eliminar un rol que tiene usuarios asignados');
      return;
    }

    if (window.confirm('¿Estás seguro de eliminar este rol?')) {
      try {
        // Aquí se enviaría a la API
        showSuccess('Rol eliminado correctamente');
        loadRoles();
      } catch (error) {
        showError('Error al eliminar el rol');
      }
    }
  };

  const getPermissionNames = (permissions) => {
    if (permissions.includes('all')) {
      return 'Todos los permisos';
    }
    return permissions
      .map(p => systemPermissions.find(sp => sp.id === p)?.name)
      .filter(Boolean)
      .join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Roles y Permisos
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona los roles del sistema y sus permisos asociados
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nuevo Rol</span>
        </button>
      </div>

      {/* Lista de roles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {roles.map((role) => (
          <div
            key={role.id}
            className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className="text-lg font-semibold text-gray-900">{role.name}</h4>
                  {role.isSystem && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      Sistema
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{role.description}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleOpenModal(role)}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                  title="Editar rol"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                {!role.isSystem && (
                  <button
                    onClick={() => handleDeleteRole(role.id)}
                    className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                    title="Eliminar rol"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="font-medium">{role.userCount}</span>
                <span className="ml-1">usuarios con este rol</span>
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Permisos:</span>
                <p className="mt-1 line-clamp-2">{getPermissionNames(role.permissions)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal para crear/editar rol */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {editingRole ? 'Editar Rol' : 'Crear Nuevo Rol'}
              </h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Nombre del rol */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Rol *
                </label>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ej: Coordinador Académico"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe las responsabilidades de este rol..."
                />
              </div>

              {/* Permisos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Permisos *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
                  {systemPermissions.map((permission) => (
                    <div
                      key={permission.id}
                      className={`flex items-start space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedPermissions.includes(permission.id)
                          ? 'bg-primary-50 border-primary-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => handleTogglePermission(permission.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={() => {}}
                        className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {permission.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {permission.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveRole}
                disabled={!roleName.trim() || selectedPermissions.length === 0}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingRole ? 'Guardar Cambios' : 'Crear Rol'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPermissions;
